import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BellRing,
  Building2,
  CalendarDays,
  Check,
  ChevronDown,
  ClipboardList,
  ClipboardPenLine,
  FileHeart,
  HeartPulse,
  LoaderCircle,
  LogOut,
  Menu,
  PawPrint,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Stethoscope,
  Syringe,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  BrowserRouter,
  Link,
  Navigate,
  NavLink,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  api,
  ApiError,
  type AccessScope,
  type AppointmentInput,
  type AppointmentStatus,
  type CrmAccess,
  type CrmAppointment,
  type CrmDashboard,
  type CrmLocation,
  type CrmPatient,
  type CrmPatientDetail,
  type ConsultationInput,
  type MedicationInput,
  type VaccinationInput,
  type ProWorkspaceType,
  type PatientHistoryCollection,
} from "./api";
import { AuthProvider, useAuth } from "./auth-context";
import { ClinicalActComposer } from "./clinical-act";
import { ClinicalDraftsScreen } from "./clinical-drafts";
import { ClinicalDocuments, MedicationPanel, VaccinationComposer } from "./clinical-followup";
import { CrmProvider, useCrm } from "./crm-context";
import { OperationsScreen } from "./operations";
import { TeamInvitationAcceptScreen, TeamScreen } from "./team";
import { LocationsScreen } from "./locations";
import { TasksScreen } from "./tasks";
import { workspaceDefinition } from "./workspace-registry";
import WorkspaceSettingsScreen from "./workspace-settings";
import { VeterinaryAccessScreen } from "./veterinary-access";
import { safeReturnPath } from "./return-path";
import { HealthFields, HealthSection, VALUE_LABELS } from "./health-record-fields";
import { nextPatientHistoryQuery, mergePatientHistory } from "./patient-history";
import { PatientHealthDocuments } from "./patient-health-documents";

const CommercialWorkspaceScreen = lazy(() => import("./commercial-workspace"));
const OperationalWorkspaceScreen = lazy(() => import("./operational-workspace"));
const ShelterWorkspaceScreen = lazy(() => import("./shelter-workspace"));
const ProfessionalOnboarding = lazy(() => import("./professional-onboarding"));
const AvailabilityScreen = lazy(() => import("./availability"));

const ROLE_LABEL: Record<string, string> = {
  OWNER: "Responsable",
  ADMIN: "Administración",
  VETERINARIAN: "Veterinario/a",
  ASSISTANT: "Asistencia",
  RECEPTION: "Recepción",
  AUDITOR: "Auditoría",
  EDITOR: "Edición",
  ANALYST: "Analítica",
};

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  PLANNED: "Planificado",
  CONFIRMED: "Confirmado",
  CHECKED_IN: "En espera",
  IN_PROGRESS: "En atención",
  COMPLETED: "Finalizado",
  CANCELLED: "Cancelado",
  NO_SHOW: "Ausente",
};

const SCOPE_LABEL: Record<AccessScope, string> = {
  READ_ONLY: "Solo lectura",
  CLINICAL: "Atención clínica",
  PRIMARY: "Veterinaria habitual",
};

function formatDate(value: string, includeTime = false): string {
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" as const } : {}),
  }).format(new Date(value));
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function patientKind(species: "DOG" | "CAT"): string {
  return species === "DOG" ? "Perro" : "Gato";
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof ApiError || reason instanceof Error ? reason.message : fallback;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function Brand({
  compact = false,
  subtitle = "Plataforma profesional",
}: {
  compact?: boolean;
  subtitle?: string;
}): ReactNode {
  return (
    <span className={`brand${compact ? " brand-compact" : ""}`}>
      <span>Tappet</span>
      {!compact && <small>{subtitle}</small>}
    </span>
  );
}

function LoadingScreen({ label = "Preparando el espacio profesional…" }: { label?: string }) {
  return (
    <main className="loading-screen" role="status">
      <Brand />
      <LoaderCircle className="spin" size={25} aria-hidden="true" />
      <p>{label}</p>
    </main>
  );
}

function ErrorPanel({ message, retry }: { message: string; retry?: () => void }) {
  return (
    <div className="error-panel" role="alert">
      <AlertTriangle size={20} aria-hidden="true" />
      <div>
        <strong>No pudimos completar la carga</strong>
        <p>{message}</p>
      </div>
      {retry && (
        <button className="button button-secondary button-compact" onClick={retry}>
          <RefreshCw size={16} /> Reintentar
        </button>
      )}
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  text,
  action,
}: {
  icon: LucideIcon;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <span aria-hidden="true">
        <Icon size={27} />
      </span>
      <h2>{title}</h2>
      <p>{text}</p>
      {action}
    </section>
  );
}

function PageHeader({
  eyebrow,
  title,
  text,
  action,
}: {
  eyebrow: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
      {action && <div className="page-header-action">{action}</div>}
    </header>
  );
}

function LoginScreen(): ReactNode {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const requestedPath = safeReturnPath((location.state as { from?: unknown } | null)?.from);

  if (status === "loading") return <LoadingScreen />;
  if (status === "authenticated") return <Navigate to={requestedPath} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      void navigate(requestedPath, { replace: true });
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos iniciar sesión."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-story" aria-label="Tappet para profesionales">
        <Brand />
        <div className="login-story-copy">
          <span className="eyebrow eyebrow-inverse">Tappet Pro</span>
          <h1>Tu operación profesional, en un solo lugar.</h1>
          <p>
            Agenda, clientes, servicios, catálogo y seguimiento en espacios de trabajo conectados
            con el ecosistema Tappet.
          </p>
        </div>
        <div className="login-story-pulse" aria-hidden="true">
          <span />
          <HeartPulse size={34} />
          <span />
        </div>
        <p className="login-story-note">
          Cada rol accede únicamente a las capacidades habilitadas para su organización.
        </p>
      </section>
      <section className="login-panel">
        <form className="login-card" onSubmit={(event) => void submit(event)}>
          <Brand compact />
          <div>
            <span className="eyebrow">Acceso profesional</span>
            <h2>Ingresá al CRM</h2>
            <p>Usá la cuenta vinculada a tu organización profesional.</p>
          </div>
          {error && <ErrorPanel message={error} />}
          <label>
            Correo
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            Contraseña
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          </label>
          <button className="button button-primary" disabled={busy}>
            {busy ? <LoaderCircle className="spin" size={18} /> : <ShieldCheck size={18} />}
            {busy ? "Ingresando…" : "Ingresar de forma segura"}
          </button>
          <p className="login-help">
            ¿Todavía no registraste tu organización?{" "}
            <a href="https://app.tappet.com.ar/">Iniciá el alta profesional</a>
          </p>
        </form>
      </section>
    </main>
  );
}

function WorkspaceSelectionScreen(): ReactNode {
  const { status: authStatus, logout } = useAuth();
  const crm = useCrm();
  const navigate = useNavigate();
  const [busyChoice, setBusyChoice] = useState("");
  const [selectionError, setSelectionError] = useState<string | null>(null);

  if (authStatus === "loading" || crm.status === "loading") return <LoadingScreen />;
  if (authStatus === "anonymous") return <Navigate to="/ingresar" replace />;
  if (crm.status === "error") {
    return (
      <main className="standalone-state">
        <Brand />
        <ErrorPanel
          message={crm.error ?? "No pudimos cargar Tappet Pro."}
          retry={() => void crm.reload()}
        />
      </main>
    );
  }
  if (!crm.context?.organizations.length) return <Navigate to="/" replace />;
  if (crm.activeWorkspace && !crm.requiresSelection) {
    return <Navigate to={workspaceDefinition(crm.activeWorkspace.type).homeRoute} replace />;
  }

  async function choose(organizationId: string, workspace: ProWorkspaceType): Promise<void> {
    const choice = `${organizationId}:${workspace}`;
    setBusyChoice(choice);
    setSelectionError(null);
    try {
      await crm.selectWorkspace(organizationId, workspace);
      void navigate(workspaceDefinition(workspace).homeRoute, { replace: true });
    } catch (reason) {
      setSelectionError(errorMessage(reason, "No pudimos abrir ese espacio de trabajo."));
    } finally {
      setBusyChoice("");
    }
  }

  return (
    <main className="workspace-selector-page">
      <header className="workspace-selector-header">
        <Brand />
        <div>
          <span className="eyebrow">Contexto profesional</span>
          <h1>¿Dónde querés trabajar?</h1>
          <p>
            Cada espacio conserva su operación y sus permisos. Podés cambiarlo después sin duplicar
            tu cuenta.
          </p>
        </div>
      </header>
      {selectionError && (
        <div className="error-panel" role="alert">
          <AlertTriangle size={20} />
          <p>{selectionError}</p>
        </div>
      )}
      <section className="workspace-choice-grid" aria-label="Organizaciones y espacios disponibles">
        {crm.context.organizations.flatMap((organization) =>
          organization.availableWorkspaces.map((workspace) => {
            const definition = workspaceDefinition(workspace.type);
            const Icon = definition.icon;
            const choice = `${organization.id}:${workspace.type}`;
            return (
              <button
                className="workspace-choice-card"
                key={choice}
                style={{ "--choice-accent": definition.accent } as CSSProperties}
                disabled={Boolean(busyChoice)}
                onClick={() => void choose(organization.id, workspace.type)}
              >
                <span className="workspace-choice-icon">
                  <Icon size={25} />
                </span>
                <span className="workspace-choice-copy">
                  <small>{organization.name}</small>
                  <strong>{definition.label}</strong>
                  <span>{definition.description}</span>
                </span>
                <span className="workspace-choice-role">{ROLE_LABEL[workspace.role]}</span>
                {busyChoice === choice ? (
                  <LoaderCircle className="spin" size={19} />
                ) : (
                  <ArrowRight size={19} />
                )}
              </button>
            );
          }),
        )}
      </section>
      <footer className="workspace-selector-footer">
        <a href="https://app.tappet.com.ar/">Ir a Mis mascotas</a>
        <button onClick={() => void logout().then(() => navigate("/ingresar"))}>
          Cerrar sesión
        </button>
      </footer>
    </main>
  );
}

function Shell(): ReactNode {
  const { status, user, logout } = useAuth();
  const crm = useCrm();
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [workspaceAccent, setWorkspaceAccent] = useState<string | null>(null);
  const isCompactNavigation = useMediaQuery("(max-width: 900px)");
  const menuCloseRef = useRef<HTMLButtonElement>(null);

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (menuOpen && isCompactNavigation) menuCloseRef.current?.focus();
  }, [isCompactNavigation, menuOpen]);
  useEffect(() => {
    const organizationId = crm.activeProOrganization?.id;
    const workspace = crm.activeWorkspace?.type;
    if (!organizationId || !workspace) {
      setWorkspaceAccent(null);
      return;
    }
    let alive = true;
    void api
      .workspaceSettings(organizationId, workspace)
      .then((settings) => {
        if (alive) setWorkspaceAccent(settings.settings.branding?.accentColor ?? null);
      })
      .catch(() => {
        if (alive) setWorkspaceAccent(null);
      });
    return () => {
      alive = false;
    };
  }, [crm.activeProOrganization?.id, crm.activeWorkspace?.type]);
  useEffect(() => {
    const update = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          organizationId: string;
          workspace: ProWorkspaceType;
          accentColor: string;
        }>
      ).detail;
      if (
        detail?.organizationId === crm.activeProOrganization?.id &&
        detail.workspace === crm.activeWorkspace?.type
      )
        setWorkspaceAccent(detail.accentColor);
    };
    window.addEventListener("tappet-workspace-branding", update);
    return () => window.removeEventListener("tappet-workspace-branding", update);
  }, [crm.activeProOrganization?.id, crm.activeWorkspace?.type]);

  if (status === "loading" || crm.status === "loading") return <LoadingScreen />;
  if (status === "anonymous") {
    return (
      <Navigate
        to="/ingresar"
        replace
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }
  if (crm.status === "error") {
    return (
      <main className="standalone-state">
        <Brand />
        <ErrorPanel
          message={crm.error ?? "No pudimos cargar el CRM."}
          retry={() => void crm.reload()}
        />
      </main>
    );
  }
  if (crm.requiresSelection) return <Navigate to="/seleccionar" replace />;
  if (!crm.activeOrganization) {
    return (
      <main className="standalone-state">
        <Brand />
        <Suspense fallback={<LoadingScreen />}>
          <ProfessionalOnboarding />
        </Suspense>
        <button
          className="button button-secondary"
          onClick={() => void logout().then(() => navigate("/ingresar"))}
        >
          <LogOut size={17} /> Cerrar sesión
        </button>
      </main>
    );
  }

  const activeWorkspace = crm.activeWorkspace;
  if (!activeWorkspace) return <Navigate to="/seleccionar" replace />;
  const definition = workspaceDefinition(activeWorkspace.type);
  const navItems = definition.navigation.filter(
    (item) => item.implemented !== false && activeWorkspace.capabilities.includes(item.capability),
  );
  const mobileNavItems = navItems.filter((item) => item.mobile).slice(0, 5);

  async function changeOrganization(nextOrganizationId: string): Promise<void> {
    const organization = crm.context?.organizations.find((item) => item.id === nextOrganizationId);
    const workspace =
      organization?.availableWorkspaces.find(
        (item) => item.type === organization.primaryVertical,
      ) ?? organization?.availableWorkspaces[0];
    if (!organization || !workspace) return;
    await crm.selectWorkspace(organization.id, workspace.type);
    void navigate(workspaceDefinition(workspace.type).homeRoute);
  }

  async function changeWorkspace(nextWorkspace: ProWorkspaceType): Promise<void> {
    if (!crm.activeProOrganization) return;
    await crm.selectWorkspace(crm.activeProOrganization.id, nextWorkspace);
    void navigate(workspaceDefinition(nextWorkspace).homeRoute);
  }

  return (
    <div
      className={`crm-shell workspace-${activeWorkspace.type.toLowerCase()}`}
      style={{ "--workspace-accent": workspaceAccent ?? definition.accent } as CSSProperties}
    >
      <aside
        className={`crm-sidebar${menuOpen ? " crm-sidebar-open" : ""}`}
        aria-hidden={isCompactNavigation && !menuOpen ? "true" : undefined}
        inert={isCompactNavigation && !menuOpen ? true : undefined}
      >
        <div className="crm-sidebar-head">
          <Link to={definition.homeRoute} aria-label={`Ir al inicio de ${definition.label}`}>
            <Brand subtitle={definition.shortLabel} />
          </Link>
          <button
            ref={menuCloseRef}
            className="icon-button crm-menu-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Cerrar navegación"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="crm-nav" aria-label="Navegación profesional">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} {...(end ? { end: true } : {})}>
              <Icon size={19} aria-hidden="true" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="crm-sidebar-trust">
          <ShieldCheck size={19} aria-hidden="true" />
          <div>
            <strong>Contexto protegido</strong>
            <p>
              {activeWorkspace.type === "VETERINARY"
                ? "Solo pacientes con vínculo válido."
                : "Permisos validados por workspace."}
            </p>
          </div>
        </div>
        <div className="crm-user">
          <span aria-hidden="true">{user?.displayName.charAt(0).toUpperCase()}</span>
          <div>
            <strong>{user?.displayName}</strong>
            <small>{ROLE_LABEL[crm.activeOrganization.role]}</small>
          </div>
          <button
            className="icon-button"
            onClick={() => void logout().then(() => navigate("/ingresar"))}
            aria-label="Cerrar sesión"
          >
            <LogOut size={18} />
          </button>
        </div>
      </aside>
      {menuOpen && (
        <button
          className="crm-overlay"
          aria-label="Cerrar navegación"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="crm-frame">
        <header className="crm-topbar">
          <button
            className="icon-button crm-menu-open"
            onClick={() => setMenuOpen(true)}
            aria-label="Abrir navegación"
          >
            <Menu size={21} />
          </button>
          <label className="organization-switcher">
            <span>Organización activa</span>
            <select
              value={crm.activeOrganization.id}
              onChange={(event) => void changeOrganization(event.target.value)}
            >
              {crm.context?.organizations.map((organization) => (
                <option value={organization.id} key={organization.id}>
                  {organization.name}
                </option>
              ))}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </label>
          <label className="organization-switcher workspace-switcher">
            <span>Espacio de trabajo</span>
            <select
              value={activeWorkspace.type}
              onChange={(event) => void changeWorkspace(event.target.value as ProWorkspaceType)}
            >
              {crm.activeProOrganization?.availableWorkspaces.map((workspace) => (
                <option value={workspace.type} key={workspace.type}>
                  {workspaceDefinition(workspace.type).shortLabel}
                </option>
              ))}
            </select>
            <ChevronDown size={16} aria-hidden="true" />
          </label>
          <div className="connection-status">
            <span aria-hidden="true" />
            En línea
          </div>
          {activeWorkspace.type === "VETERINARY" &&
            activeWorkspace.capabilities.includes("CLINICAL_WRITE") && (
              <Link className="mode-link mode-link-attention" to="/atenciones">
                <Stethoscope size={17} /> Modo atención
              </Link>
            )}
          <a className="mode-link" href="https://app.tappet.com.ar/">
            <UserRound size={17} /> Modo tutor
          </a>
        </header>
        <main className="crm-main" id="main-content">
          <Outlet />
        </main>
      </div>
      <nav
        className="crm-bottom-nav"
        aria-label="Navegación profesional móvil"
        aria-hidden={isCompactNavigation && menuOpen ? "true" : undefined}
        inert={isCompactNavigation && menuOpen ? true : undefined}
      >
        {mobileNavItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} {...(end ? { end: true } : {})}>
            <Icon size={20} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  detail: string;
  tone: "navy" | "pink" | "green" | "amber";
}) {
  return (
    <article className={`metric-card metric-card-${tone}`}>
      <span aria-hidden="true">
        <Icon size={20} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

function AppointmentRow({
  appointment,
  actions,
}: {
  appointment: CrmAppointment;
  actions?: ReactNode;
}) {
  return (
    <article className="appointment-row">
      <time dateTime={appointment.startsAt}>
        <strong>{formatTime(appointment.startsAt)}</strong>
        <span>{appointment.durationMinutes} min</span>
      </time>
      <span className="appointment-rail" aria-hidden="true" />
      <div className="appointment-copy">
        <div>
          <h3>{appointment.petName}</h3>
          <span className={`status status-${appointment.status.toLowerCase()}`}>
            {STATUS_LABEL[appointment.status]}
          </span>
        </div>
        <p>{appointment.title}</p>
        <small>{appointment.location ?? "Sin ubicación indicada"}</small>
      </div>
      {actions && <div className="appointment-actions">{actions}</div>}
    </article>
  );
}

function DashboardScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const canOpenClinicalRecord = activeOrganization?.role !== "RECEPTION";
  const [data, setData] = useState<CrmDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.dashboard(activeOrganization.id));
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos cargar el panel."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!activeOrganization) return null;
  return (
    <div className="screen dashboard-screen">
      <PageHeader
        eyebrow="Operación de hoy"
        title={`Buen día en ${activeOrganization.name}`}
        text="Lo importante de la jornada, sin perder de vista la continuidad de cada paciente."
        action={
          <button
            className="button button-secondary button-compact"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className={loading ? "spin" : undefined} size={16} /> Actualizar
          </button>
        }
      />
      {error && <ErrorPanel message={error} retry={() => void load()} />}
      {loading && !data ? (
        <div className="dashboard-skeleton" role="status" aria-label="Cargando panel">
          {Array.from({ length: 4 }, (_, index) => (
            <span key={index} />
          ))}
        </div>
      ) : data ? (
        <>
          <section className="metric-grid" aria-label="Resumen operativo">
            <MetricCard
              icon={PawPrint}
              label="Pacientes activos"
              value={data.metrics.patientCount}
              detail="con vínculo vigente"
              tone="navy"
            />
            <MetricCard
              icon={CalendarDays}
              label="Turnos de hoy"
              value={data.metrics.todayAppointmentCount}
              detail="en esta organización"
              tone="pink"
            />
            <MetricCard
              icon={Syringe}
              label="Vacunas próximas"
              value={data.metrics.dueVaccinationCount}
              detail="dentro de 30 días"
              tone="green"
            />
            <MetricCard
              icon={BellRing}
              label="Solicitudes"
              value={data.metrics.pendingAccessCount}
              detail="esperando al tutor"
              tone="amber"
            />
            <MetricCard
              icon={ClipboardPenLine}
              label="Borradores activos"
              value={data.metrics.activeDraftCount}
              detail="sin firma final"
              tone="navy"
            />
          </section>
          <div className="dashboard-grid">
            <section className="panel" aria-labelledby="today-title">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">Agenda activa</span>
                  <h2 id="today-title">Turnos de hoy</h2>
                </div>
                <Link to="/agenda">
                  Ver agenda <ArrowRight size={16} />
                </Link>
              </div>
              {data.todayAppointments.length > 0 ? (
                <div className="appointment-list">
                  {data.todayAppointments.map((appointment) => (
                    <AppointmentRow appointment={appointment} key={appointment.id} />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={CalendarDays}
                  title="Jornada sin turnos"
                  text="Podés crear el próximo turno desde Agenda."
                  action={
                    <Link className="button button-secondary button-compact" to="/agenda">
                      <Plus size={16} /> Crear turno
                    </Link>
                  }
                />
              )}
            </section>
            <aside className="panel recent-patients" aria-labelledby="recent-title">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">Continuidad</span>
                  <h2 id="recent-title">Pacientes recientes</h2>
                </div>
              </div>
              {data.recentPatients.length > 0 ? (
                <div className="recent-patient-list">
                  {data.recentPatients.map((patient) => (
                    <Link
                      to={
                        canOpenClinicalRecord
                          ? `/pacientes/${patient.id}`
                          : `/agenda?patient=${patient.id}`
                      }
                      key={patient.id}
                    >
                      <span className="patient-avatar" aria-hidden="true">
                        {patient.name.charAt(0).toUpperCase()}
                      </span>
                      <span>
                        <strong>{patient.name}</strong>
                        <small>
                          {patientKind(patient.species)} · {patient.breed ?? "Sin raza indicada"}
                        </small>
                      </span>
                      <ArrowRight size={16} />
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="muted">Los pacientes autorizados van a aparecer acá.</p>
              )}
              <Link className="button button-primary" to="/vinculos">
                <Plus size={17} /> Vincular paciente
              </Link>
            </aside>
          </div>
        </>
      ) : null}
    </div>
  );
}

function PatientCard({ patient }: { patient: CrmPatient }) {
  return (
    <Link
      className="patient-card"
      to={
        patient.clinicalDetailsAvailable
          ? `/pacientes/${patient.id}`
          : `/agenda?patient=${patient.id}`
      }
    >
      <div className="patient-card-head">
        <span className="patient-avatar patient-avatar-large" aria-hidden="true">
          {patient.name.charAt(0).toUpperCase()}
        </span>
        <div>
          <h2>{patient.name}</h2>
          <p>
            {patientKind(patient.species)} · {patient.breed ?? "Sin raza indicada"}
          </p>
        </div>
        {patient.isLost && <span className="status status-alert">Alerta activa</span>}
      </div>
      {patient.clinicalDetailsAvailable ? (
        <dl className="patient-card-facts">
          <div>
            <dt>Peso</dt>
            <dd>{patient.weightKg ? `${patient.weightKg} kg` : "Sin dato"}</dd>
          </div>
          <div>
            <dt>Consultas</dt>
            <dd>{patient.consultationCount}</dd>
          </div>
          <div>
            <dt>Vacunas</dt>
            <dd>{patient.vaccinationCount}</dd>
          </div>
        </dl>
      ) : (
        <p className="patient-card-restricted">
          Perfil operativo habilitado. La información clínica está reservada al equipo autorizado.
        </p>
      )}
      <div className="patient-card-footer">
        <span className="status status-neutral">{SCOPE_LABEL[patient.scope]}</span>
        <span>
          {patient.clinicalDetailsAvailable ? "Ver ficha" : "Agendar"} <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  );
}

function PatientsScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [patients, setPatients] = useState<CrmPatient[]>([]);
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      setPatients(await api.patients(activeOrganization.id, submittedSearch));
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos cargar los pacientes."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, submittedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSubmittedSearch(search.trim());
  }

  return (
    <div className="screen">
      <PageHeader
        eyebrow="Cartera autorizada"
        title="Pacientes"
        text="Solo aparecen mascotas con vínculo aceptado por su tutor principal."
        action={
          <Link className="button button-primary" to="/vinculos">
            <Plus size={17} /> Vincular paciente
          </Link>
        }
      />
      <form className="patient-search" role="search" onSubmit={submit}>
        <Search size={19} aria-hidden="true" />
        <label htmlFor="patient-search">Buscar por nombre, tag o código público</label>
        <input
          id="patient-search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Ej. Mora o TAP-2048"
        />
        <button className="button button-secondary button-compact">Buscar</button>
      </form>
      {error && <ErrorPanel message={error} retry={() => void load()} />}
      {loading ? (
        <div className="card-grid skeleton-grid" role="status" aria-label="Cargando pacientes">
          <span />
          <span />
          <span />
        </div>
      ) : patients.length > 0 ? (
        <>
          <p className="results-count">
            {patients.length} {patients.length === 1 ? "paciente" : "pacientes"}
          </p>
          <div className="card-grid">
            {patients.map((patient) => (
              <PatientCard patient={patient} key={patient.id} />
            ))}
          </div>
        </>
      ) : (
        <EmptyState
          icon={PawPrint}
          title={
            submittedSearch ? "No encontramos coincidencias" : "Todavía no hay pacientes vinculados"
          }
          text={
            submittedSearch
              ? "Probá con otro nombre, tag o código."
              : "Solicitá el vínculo con un código público. El tutor debe aprobarlo antes de compartir la ficha."
          }
          action={
            submittedSearch ? (
              <button
                className="button button-secondary"
                onClick={() => {
                  setSearch("");
                  setSubmittedSearch("");
                }}
              >
                Limpiar búsqueda
              </button>
            ) : (
              <Link className="button button-primary" to="/vinculos">
                <Plus size={17} /> Solicitar vínculo
              </Link>
            )
          }
        />
      )}
    </div>
  );
}

export function PatientDetailScreen(): ReactNode {
  const { petId = "" } = useParams();
  const { activeOrganization } = useCrm();
  const [detail, setDetail] = useState<CrmPatientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [clinicalNotice, setClinicalNotice] = useState<string | null>(null);
  const [clinicalError, setClinicalError] = useState<string | null>(null);
  const [clinicalAction, setClinicalAction] = useState<{
    consultationId: string;
    mode: "ADDENDUM" | "VOID";
    idempotencyKey: string;
  } | null>(null);
  const [clinicalActionText, setClinicalActionText] = useState("");
  const [clinicalActionPending, setClinicalActionPending] = useState(false);
  const [vaccinationComposerOpen, setVaccinationComposerOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState<PatientHistoryCollection | null>(null);
  const currentPatientContext = useRef("");
  const loadRevision = useRef(0);
  currentPatientContext.current = `${activeOrganization?.id ?? ""}:${petId}`;

  const load = useCallback(async () => {
    if (!activeOrganization || !petId) return;
    const revision = ++loadRevision.current;
    setLoading(true);
    setDetail(null);
    setLoadingHistory(null);
    setError(null);
    try {
      const result = await api.patient(activeOrganization.id, petId);
      if (loadRevision.current === revision) setDetail(result);
    } catch (reason) {
      if (loadRevision.current === revision)
        setError(errorMessage(reason, "No pudimos cargar la ficha."));
    } finally {
      if (loadRevision.current === revision) setLoading(false);
    }
  }, [activeOrganization?.id, petId]);

  useEffect(() => void load(), [load]);

  async function loadOlderHistory(kind: PatientHistoryCollection): Promise<void> {
    if (!detail || !activeOrganization || loadingHistory) return;
    const query = nextPatientHistoryQuery(detail, kind);
    if (!query) return;
    const requestedContext = currentPatientContext.current;
    setLoadingHistory(kind);
    setClinicalError(null);
    try {
      const next = await api.patient(activeOrganization.id, petId, query);
      if (requestedContext === currentPatientContext.current)
        setDetail((current) => (current ? mergePatientHistory(current, next, kind) : null));
    } catch (reason) {
      if (requestedContext === currentPatientContext.current) {
        if (reason instanceof ApiError && [401, 403].includes(reason.status)) {
          setDetail(null);
          setError(errorMessage(reason, "El acceso a esta ficha ya no está disponible."));
        } else
          setClinicalError(errorMessage(reason, "No pudimos cargar los registros anteriores."));
      }
    } finally {
      if (requestedContext === currentPatientContext.current) setLoadingHistory(null);
    }
  }

  const replaceConsultation = useCallback(
    (consultation: CrmPatientDetail["consultations"][number]) => {
      setDetail((current) =>
        current
          ? {
              ...current,
              consultations: current.consultations.some((item) => item.id === consultation.id)
                ? current.consultations.map((item) =>
                    item.id === consultation.id ? consultation : item,
                  )
                : [consultation, ...current.consultations],
            }
          : current,
      );
    },
    [],
  );

  const updateAttachmentCount = useCallback((consultationId: string, count: number) => {
    setDetail((current) =>
      current
        ? {
            ...current,
            consultations: current.consultations.map((consultation) =>
              consultation.id === consultationId
                ? { ...consultation, attachmentCount: count }
                : consultation,
            ),
          }
        : current,
    );
  }, []);

  async function signConsultation(input: ConsultationInput, idempotencyKey: string): Promise<void> {
    if (!activeOrganization) return;
    setClinicalError(null);
    const consultation = await api.createConsultation(
      activeOrganization.id,
      petId,
      input,
      idempotencyKey,
    );
    replaceConsultation(consultation);
    setComposerOpen(false);
    setClinicalNotice("Atención firmada y agregada a la historia clínica.");
  }

  function openClinicalAction(consultationId: string, mode: "ADDENDUM" | "VOID"): void {
    setClinicalNotice(null);
    setClinicalError(null);
    setClinicalActionText("");
    setClinicalAction({ consultationId, mode, idempotencyKey: crypto.randomUUID() });
  }

  async function submitClinicalAction(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!activeOrganization || !clinicalAction || clinicalActionText.trim().length < 2) return;
    setClinicalActionPending(true);
    setClinicalError(null);
    try {
      const consultation =
        clinicalAction.mode === "ADDENDUM"
          ? await api.addConsultationAddendum(
              activeOrganization.id,
              petId,
              clinicalAction.consultationId,
              clinicalActionText.trim(),
              clinicalAction.idempotencyKey,
            )
          : await api.voidConsultation(
              activeOrganization.id,
              petId,
              clinicalAction.consultationId,
              clinicalActionText.trim(),
              clinicalAction.idempotencyKey,
            );
      replaceConsultation(consultation);
      setClinicalNotice(
        clinicalAction.mode === "ADDENDUM"
          ? "Adenda firmada y anexada sin modificar el registro original."
          : "Atención anulada. La evidencia original permanece en la historia.",
      );
      setClinicalAction(null);
      setClinicalActionText("");
    } catch (reason) {
      setClinicalError(errorMessage(reason, "No pudimos completar la operación clínica."));
    } finally {
      setClinicalActionPending(false);
    }
  }

  async function createVaccination(
    input: VaccinationInput,
    idempotencyKey: string,
  ): Promise<CrmPatientDetail["vaccinations"][number]> {
    if (!activeOrganization) throw new Error("No hay una organización activa.");
    setClinicalError(null);
    const vaccination = await api.createVaccination(
      activeOrganization.id,
      petId,
      input,
      idempotencyKey,
    );
    setDetail((current) =>
      current ? { ...current, vaccinations: [vaccination, ...current.vaccinations] } : current,
    );
    setVaccinationComposerOpen(false);
    setClinicalNotice(
      vaccination.nextDueAt
        ? "Vacuna registrada. El próximo refuerzo quedó incorporado al seguimiento."
        : "Vacuna registrada en la ficha preventiva.",
    );
    return vaccination;
  }

  async function createMedication(
    input: MedicationInput,
    idempotencyKey: string,
  ): Promise<CrmPatientDetail["medications"][number]> {
    if (!activeOrganization) throw new Error("No hay una organización activa.");
    setClinicalError(null);
    const medication = await api.createMedication(
      activeOrganization.id,
      petId,
      input,
      idempotencyKey,
    );
    setDetail((current) =>
      current ? { ...current, medications: [medication, ...current.medications] } : current,
    );
    setClinicalNotice("La indicación quedó firmada y vinculada a la atención de origen.");
    return medication;
  }

  async function stopMedication(
    medicationId: string,
    input: { status: "COMPLETED" | "DISCONTINUED"; reason: string },
    idempotencyKey: string,
  ): Promise<CrmPatientDetail["medications"][number]> {
    if (!activeOrganization) throw new Error("No hay una organización activa.");
    setClinicalError(null);
    const medication = await api.stopMedication(
      activeOrganization.id,
      petId,
      medicationId,
      input,
      idempotencyKey,
    );
    setDetail((current) =>
      current
        ? {
            ...current,
            medications: current.medications.map((item) =>
              item.id === medication.id ? medication : item,
            ),
          }
        : current,
    );
    setClinicalNotice(
      medication.status === "COMPLETED"
        ? "El tratamiento quedó registrado como completado."
        : "La discontinuación y su motivo quedaron registrados.",
    );
    return medication;
  }

  if (loading && !detail) {
    return (
      <div className="screen">
        <Link className="back-link" to="/pacientes">
          ← Volver a pacientes
        </Link>
        <div className="inline-loading" role="status">
          <LoaderCircle className="spin" size={22} aria-hidden="true" />
          <span>Abriendo ficha autorizada…</span>
        </div>
      </div>
    );
  }
  if (error)
    return (
      <div className="screen">
        <Link className="back-link" to="/pacientes">
          ← Volver a pacientes
        </Link>
        <ErrorPanel message={error} retry={() => void load()} />
      </div>
    );
  if (!detail) return null;
  const summary = detail.medicalSummary;
  return (
    <div className="screen patient-detail-screen">
      <Link className="back-link" to="/pacientes">
        ← Volver a pacientes
      </Link>
      <header className="patient-hero">
        <span className="patient-avatar patient-avatar-xl" aria-hidden="true">
          {detail.patient.name.charAt(0).toUpperCase()}
        </span>
        <div>
          <span className="eyebrow">Ficha clínica longitudinal</span>
          <h1>{detail.patient.name}</h1>
          <p>
            {patientKind(detail.patient.species)} · {detail.patient.breed ?? "Sin raza indicada"} ·{" "}
            {detail.patient.sex ?? "Sexo sin indicar"}
          </p>
        </div>
        <div className="patient-hero-status">
          <span className="status status-active">
            <ShieldCheck size={14} /> {SCOPE_LABEL[detail.access.scope]}
          </span>
          {detail.patient.activeTagCode && <code>{detail.patient.activeTagCode}</code>}
          {detail.capabilities.canWriteClinical && (
            <button
              className="button button-primary button-compact"
              type="button"
              onClick={() => {
                setClinicalNotice(null);
                setClinicalError(null);
                setComposerOpen(true);
              }}
              aria-expanded={composerOpen}
            >
              <Plus size={16} /> Nueva atención
            </button>
          )}
        </div>
      </header>
      {detail.patient.isLost && (
        <div className="critical-banner" role="alert">
          <AlertTriangle size={20} />
          <strong>Esta mascota tiene una alerta de pérdida activa.</strong>
        </div>
      )}
      <div className="clinical-live-region" aria-live="polite">
        {clinicalNotice && (
          <div className="success-banner" role="status">
            <Check size={18} aria-hidden="true" />
            <span>{clinicalNotice}</span>
            <button
              className="icon-button"
              type="button"
              onClick={() => setClinicalNotice(null)}
              aria-label="Cerrar confirmación"
            >
              <X size={16} />
            </button>
          </div>
        )}
        {clinicalError && (
          <div className="critical-banner" role="alert">
            <AlertTriangle size={18} aria-hidden="true" />
            <span>{clinicalError}</span>
          </div>
        )}
      </div>
      {composerOpen && detail.capabilities.signer && (
        <ClinicalActComposer
          patientName={detail.patient.name}
          signer={detail.capabilities.signer}
          onCancel={() => setComposerOpen(false)}
          onSigned={signConsultation}
        />
      )}
      <section className="clinical-summary" aria-labelledby="summary-title">
        <div className="panel-heading">
          <div>
            <span className="eyebrow">Lectura rápida</span>
            <h2 id="summary-title">Resumen clínico</h2>
          </div>
          {summary && <small>Actualizado {formatDate(summary.updatedAt)}</small>}
        </div>
        {summary ? (
          <dl className="clinical-summary-grid">
            <div>
              <dt>
                <AlertTriangle size={16} /> Alergias
              </dt>
              <dd>{summary.allergies ?? "Sin alergias registradas"}</dd>
            </div>
            <div>
              <dt>
                <AlertTriangle size={16} /> Alertas críticas
              </dt>
              <dd>{summary.criticalAlerts ?? "Sin alertas críticas registradas"}</dd>
            </div>
            <div>
              <dt>
                <Activity size={16} /> Condiciones
              </dt>
              <dd>{summary.conditions ?? "Sin condiciones registradas"}</dd>
            </div>
            <div>
              <dt>
                <ClipboardList size={16} /> Medicación
              </dt>
              <dd>{summary.medications ?? "Sin medicación registrada"}</dd>
            </div>
            <div>
              <dt>
                <HeartPulse size={16} /> Peso y sangre
              </dt>
              <dd>
                {summary.weightKg ? `${summary.weightKg} kg` : "Sin peso"} ·{" "}
                {summary.bloodType ?? "Grupo sin indicar"}
              </dd>
            </div>
            <div className="review-span-2">
              <dt>Procedencia</dt>
              <dd>
                {summary.sourceType === "TUTOR_REPORTED"
                  ? "Información aportada por el tutor"
                  : summary.sourceType === "VETERINARIAN_VERIFIED"
                    ? "Verificada por un veterinario"
                    : "Registrada por un profesional"}
              </dd>
            </div>
            {summary.notes && (
              <div className="review-span-2">
                <dt>Observaciones</dt>
                <dd>{summary.notes}</dd>
              </div>
            )}
          </dl>
        ) : (
          <p className="muted">El tutor todavía no completó el resumen médico.</p>
        )}
      </section>
      <div className="vet-access-health-grid">
        <section className="panel vet-access-section">
          <h2>Salud reproductiva</h2>
          <HealthFields record={detail.patient.reproductive} />
        </section>
        <HealthSection
          title="Medicación informada por el tutor"
          records={detail.tutorReportedMedications ?? []}
        />
        <HealthSection title="Controles preventivos" records={detail.preventiveCare} />
      </div>
      {detail.problems.length > 0 && (
        <section className="panel" aria-labelledby="clinical-problems-title">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Contexto longitudinal</span>
              <h2 id="clinical-problems-title">Problemas y antecedentes</h2>
            </div>
            <span className="count">{detail.problems.length}</span>
          </div>
          <ul className="compact-list">
            {detail.problems.map((problem) => (
              <li key={problem.id}>
                <span>
                  <strong>{problem.name}</strong>
                  <small>{problem.description ?? "Sin detalle adicional"}</small>
                  <HealthFields
                    record={problem}
                    fields={["sourceType", "authorName", "authorLicense", "onsetAt", "resolvedAt"]}
                  />
                </span>
                <span className="status status-neutral">
                  {VALUE_LABELS[problem.status] ?? problem.status.replaceAll("_", " ")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="patient-detail-grid">
        <section className="panel continuity-panel" aria-labelledby="history-title">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Línea de continuidad</span>
              <h2 id="history-title">Historia de atenciones</h2>
            </div>
            <span className="count">
              {detail.consultations.length}
              {detail.pagination ? ` / ${detail.pagination.consultations.total}` : ""}
            </span>
          </div>
          {detail.consultations.length ? (
            <ol className="continuity-line">
              {detail.consultations.map((consultation) => {
                const actionOpen = clinicalAction?.consultationId === consultation.id;
                return (
                  <li
                    key={consultation.id}
                    className={consultation.status === "VOIDED" ? "voided" : undefined}
                  >
                    <span className="continuity-marker" aria-hidden="true" />
                    <article className="consultation-record">
                      <div className="consultation-meta">
                        <time dateTime={consultation.occurredAt}>
                          {formatDate(consultation.occurredAt, true)}
                        </time>
                        {consultation.organizationId === activeOrganization?.id && (
                          <span className="verified-origin">
                            <ShieldCheck size={12} aria-hidden="true" /> Firmada en esta clínica
                          </span>
                        )}
                      </div>
                      <h3>{consultation.reason}</h3>
                      <p>
                        {consultation.diagnosis ??
                          consultation.findings ??
                          "Sin diagnóstico detallado"}
                      </p>
                      <details className="vet-access-record-details">
                        <summary>Ver registro clínico completo</summary>
                        <HealthFields
                          record={consultation}
                          fields={[
                            "anamnesis",
                            "physicalExam",
                            "findings",
                            "assessment",
                            "diagnosis",
                            "treatment",
                            "indications",
                            "sourceType",
                            "followUpAt",
                          ]}
                        />
                      </details>
                      {consultation.indications && (
                        <p>
                          <strong>Indicaciones:</strong> {consultation.indications}
                        </p>
                      )}
                      {consultation.followUpAt && (
                        <p>
                          <strong>Próximo control:</strong>{" "}
                          {formatDate(consultation.followUpAt, true)}
                        </p>
                      )}
                      <small>
                        {consultation.authorVetName}
                        {consultation.authorVetLicense
                          ? ` · Mat. ${consultation.authorVetLicense}`
                          : ""}
                        {consultation.organizationName ? ` · ${consultation.organizationName}` : ""}
                        {` · ${consultation.accessMode === "TEMPORARY_24H" ? "acceso temporal" : "vínculo autorizado"}`}
                      </small>
                      {consultation.status !== "SIGNED" && (
                        <span
                          className={`status ${
                            consultation.status === "VOIDED" ? "status-cancelled" : "status-neutral"
                          }`}
                        >
                          {consultation.status === "AMENDED" ? "Con adenda" : "Anulada"}
                        </span>
                      )}
                      {consultation.voidReason && (
                        <p className="void-reason">
                          <strong>Motivo de anulación:</strong> {consultation.voidReason}
                        </p>
                      )}
                      {consultation.amendments.length > 0 && (
                        <div className="amendment-stack" aria-label="Adendas">
                          {consultation.amendments.map((amendment) => (
                            <div key={amendment.id}>
                              <span>Adenda · {formatDate(amendment.createdAt, true)}</span>
                              <p>{amendment.content}</p>
                              <small>
                                {amendment.authorVetName}
                                {amendment.authorVetLicense
                                  ? ` · Mat. ${amendment.authorVetLicense}`
                                  : ""}
                              </small>
                            </div>
                          ))}
                        </div>
                      )}
                      {activeOrganization && (
                        <ClinicalDocuments
                          organizationId={activeOrganization.id}
                          petId={petId}
                          consultationId={consultation.id}
                          initialCount={consultation.attachmentCount}
                          canAttach={consultation.canAttach}
                          onCountChange={(count) => updateAttachmentCount(consultation.id, count)}
                        />
                      )}
                      {(consultation.canAmend || consultation.canVoid) && (
                        <div className="consultation-actions">
                          {consultation.canAmend && (
                            <button
                              className="button-link"
                              type="button"
                              onClick={() => openClinicalAction(consultation.id, "ADDENDUM")}
                              aria-expanded={actionOpen && clinicalAction?.mode === "ADDENDUM"}
                            >
                              Agregar adenda
                            </button>
                          )}
                          {consultation.canVoid && (
                            <button
                              className="button-link button-link-danger"
                              type="button"
                              onClick={() => openClinicalAction(consultation.id, "VOID")}
                              aria-expanded={actionOpen && clinicalAction?.mode === "VOID"}
                            >
                              Anular atención
                            </button>
                          )}
                        </div>
                      )}
                      {actionOpen && clinicalAction && (
                        <form
                          className="clinical-action-form"
                          onSubmit={(event) => void submitClinicalAction(event)}
                        >
                          <label>
                            <span>
                              {clinicalAction.mode === "ADDENDUM"
                                ? "Contenido de la adenda"
                                : "Motivo de la anulación"}
                            </span>
                            <textarea
                              value={clinicalActionText}
                              onChange={(event) => setClinicalActionText(event.target.value)}
                              minLength={2}
                              maxLength={clinicalAction.mode === "ADDENDUM" ? 4000 : 500}
                              rows={3}
                              autoFocus
                              required
                            />
                            <small>
                              {clinicalAction.mode === "ADDENDUM"
                                ? "Se anexará como un registro nuevo; el original no cambia."
                                : "La atención seguirá visible y conservará toda su evidencia."}
                            </small>
                          </label>
                          <div className="form-actions">
                            <button
                              className="button button-secondary button-compact"
                              type="button"
                              onClick={() => setClinicalAction(null)}
                              disabled={clinicalActionPending}
                            >
                              Cancelar
                            </button>
                            <button
                              className={`button button-compact ${
                                clinicalAction.mode === "VOID" ? "button-danger" : "button-primary"
                              }`}
                              type="submit"
                              disabled={
                                clinicalActionText.trim().length < 2 || clinicalActionPending
                              }
                            >
                              {clinicalActionPending && (
                                <LoaderCircle className="spin" size={15} aria-hidden="true" />
                              )}
                              {clinicalAction.mode === "ADDENDUM"
                                ? "Firmar adenda"
                                : "Confirmar anulación"}
                            </button>
                          </div>
                        </form>
                      )}
                    </article>
                  </li>
                );
              })}
            </ol>
          ) : (
            <EmptyState
              icon={FileHeart}
              title="Sin atenciones registradas"
              text="La historia firmada de este paciente aparecerá en esta línea."
            />
          )}
          {detail.pagination?.consultations.hasMore && (
            <button
              className="button button-secondary button-compact"
              disabled={Boolean(loadingHistory)}
              onClick={() => void loadOlderHistory("consultations")}
            >
              {loadingHistory === "consultations"
                ? "Cargando anteriores…"
                : "Ver atenciones anteriores"}
            </button>
          )}
        </section>
        <aside className="patient-side-stack">
          <MedicationPanel
            patientName={detail.patient.name}
            medications={detail.medications}
            consultations={detail.consultations}
            canWrite={detail.capabilities.canWriteClinical}
            onCreated={createMedication}
            onStopped={stopMedication}
          />
          {detail.pagination?.medications.hasMore && (
            <button
              className="button button-secondary button-compact"
              disabled={Boolean(loadingHistory)}
              onClick={() => void loadOlderHistory("medications")}
            >
              {loadingHistory === "medications" ? "Cargando indicaciones…" : "Ver más medicaciones"}
            </button>
          )}
          <section className="panel" aria-labelledby="vaccines-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Prevención</span>
                <h2 id="vaccines-title">Vacunas</h2>
              </div>
              <Syringe size={19} />
            </div>
            {detail.vaccinations.length ? (
              <ul className="compact-list">
                {detail.vaccinations.map((vaccination) => (
                  <li key={vaccination.id}>
                    <span>
                      <strong>{vaccination.name}</strong>
                      <small>Aplicada {formatDate(vaccination.administeredAt)}</small>
                      <details>
                        <summary>Ver ficha de vacunación</summary>
                        <HealthFields
                          record={vaccination}
                          fields={[
                            "providerName",
                            "professionalName",
                            "professionalLicense",
                            "manufacturer",
                            "batchNumber",
                            "route",
                            "sourceType",
                            "notes",
                          ]}
                        />
                      </details>
                    </span>
                    {vaccination.nextDueAt && (
                      <time dateTime={vaccination.nextDueAt}>
                        Próxima {formatDate(vaccination.nextDueAt)}
                      </time>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Sin vacunas registradas.</p>
            )}
            {detail.pagination?.vaccinations.hasMore && (
              <button
                className="button button-secondary button-compact"
                disabled={Boolean(loadingHistory)}
                onClick={() => void loadOlderHistory("vaccinations")}
              >
                {loadingHistory === "vaccinations" ? "Cargando vacunas…" : "Ver más vacunas"}
              </button>
            )}
            {vaccinationComposerOpen && activeOrganization ? (
              <VaccinationComposer
                patientName={detail.patient.name}
                providerName={activeOrganization.name}
                onCancel={() => setVaccinationComposerOpen(false)}
                onCreated={createVaccination}
              />
            ) : (
              detail.capabilities.canWriteClinical && (
                <button
                  className="button button-secondary button-compact"
                  type="button"
                  onClick={() => {
                    setClinicalNotice(null);
                    setClinicalError(null);
                    setVaccinationComposerOpen(true);
                  }}
                >
                  <Plus size={15} /> Registrar vacuna
                </button>
              )
            )}
          </section>
          <section className="panel" aria-labelledby="patient-agenda-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Agenda</span>
                <h2 id="patient-agenda-title">Turnos vinculados</h2>
              </div>
              <CalendarDays size={19} />
            </div>
            {detail.appointments.length ? (
              <ul className="compact-list">
                {detail.appointments.slice(0, 5).map((appointment) => (
                  <li key={appointment.id}>
                    <span>
                      <strong>{appointment.title}</strong>
                      <small>{formatDate(appointment.startsAt, true)}</small>
                    </span>
                    <span className={`status status-${appointment.status.toLowerCase()}`}>
                      {STATUS_LABEL[appointment.status]}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">Sin turnos de esta organización.</p>
            )}
            <Link
              className="button button-secondary button-compact"
              to={`/agenda?patient=${detail.patient.id}`}
            >
              <Plus size={16} /> Crear turno
            </Link>
          </section>
        </aside>
      </div>
      {activeOrganization && (
        <PatientHealthDocuments organizationId={activeOrganization.id} petId={petId} />
      )}
    </div>
  );
}

function AgendaScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPatientId = searchParams.get("patient") ?? "";
  const [appointments, setAppointments] = useState<CrmAppointment[]>([]);
  const [patients, setPatients] = useState<CrmPatient[]>([]);
  const [locations, setLocations] = useState<CrmLocation[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [petId, setPetId] = useState("");
  const [title, setTitle] = useState("Consulta veterinaria");
  const [startsAt, setStartsAt] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [locationId, setLocationId] = useState("");
  const [location, setLocation] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [reminder, setReminder] = useState<AppointmentInput["reminderMinutesBefore"]>(1440);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const patientSelectRef = useRef<HTMLSelectElement>(null);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      const [nextAppointments, nextPatients, nextLocations] = await Promise.all([
        api.appointments(activeOrganization.id, siteFilter || undefined),
        api.patients(activeOrganization.id),
        api.locations(activeOrganization.id),
      ]);
      setAppointments(nextAppointments);
      setPatients(nextPatients);
      setLocations(nextLocations);
      setPetId((current) => {
        if (
          requestedPatientId &&
          nextPatients.some((patient) => patient.id === requestedPatientId)
        ) {
          return requestedPatientId;
        }
        return current || nextPatients[0]?.id || "";
      });
      if (requestedPatientId && nextPatients.some((patient) => patient.id === requestedPatientId)) {
        setShowForm(true);
      }
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos cargar la agenda."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id, requestedPatientId, siteFilter]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (showForm) patientSelectRef.current?.focus();
  }, [showForm]);

  const upcoming = appointments.filter(
    (appointment) =>
      !["COMPLETED", "CANCELLED"].includes(appointment.status) &&
      (["CHECKED_IN", "IN_PROGRESS"].includes(appointment.status) ||
        new Date(appointment.startsAt) >= new Date()),
  );
  const history = appointments.filter(
    (appointment) =>
      ["COMPLETED", "CANCELLED", "NO_SHOW"].includes(appointment.status) ||
      (["PLANNED", "CONFIRMED"].includes(appointment.status) &&
        new Date(appointment.startsAt) < new Date()),
  );

  async function create(event: FormEvent) {
    event.preventDefault();
    if (!activeOrganization || !petId || !startsAt) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await api.createAppointment(activeOrganization.id, petId, {
        title,
        startsAt: new Date(startsAt).toISOString(),
        durationMinutes,
        ...(locationId ? { locationId } : {}),
        ...(location ? { location } : {}),
        reminderMinutesBefore: reminder,
      });
      setSuccess("El turno quedó en la agenda profesional y también será visible para el tutor.");
      setShowForm(false);
      setSearchParams({}, { replace: true });
      setStartsAt("");
      setLocationId("");
      setLocation("");
      await load();
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos crear el turno."));
    } finally {
      setBusy(false);
    }
  }

  async function transition(
    appointment: CrmAppointment,
    status: Exclude<AppointmentStatus, "PLANNED">,
  ) {
    if (!activeOrganization) return;
    if (
      (status === "CANCELLED" || status === "COMPLETED") &&
      !window.confirm(
        `${status === "CANCELLED" ? "¿Cancelar" : "¿Finalizar"} el turno de ${appointment.petName}?`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      if (status === "COMPLETED" && appointment.serviceId) {
        await api.completeProfessionalService(activeOrganization.id, appointment.id);
      } else {
        await api.transitionAppointment(activeOrganization.id, appointment.id, status);
      }
      await load();
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos actualizar el turno."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen">
      <PageHeader
        eyebrow="Operación diaria"
        title="Agenda"
        text="Turnos compartidos con el tutor y trazabilidad por organización."
        action={
          <button className="button button-primary" onClick={() => setShowForm((value) => !value)}>
            <Plus size={17} /> Nuevo turno
          </button>
        }
      />
      {error && <ErrorPanel message={error} retry={() => void load()} />}
      {success && (
        <div className="success-panel" role="status">
          <Check size={18} /> {success}
        </div>
      )}
      <section className="agenda-filter-bar" aria-label="Filtro de agenda">
        <label>
          <Building2 size={17} aria-hidden="true" />
          <span>Sede</span>
          <select value={siteFilter} onChange={(event) => setSiteFilter(event.target.value)}>
            <option value="">Todas las sedes y ubicaciones</option>
            {locations.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </label>
        {siteFilter && (
          <button className="button-link" type="button" onClick={() => setSiteFilter("")}>
            Ver toda la agenda
          </button>
        )}
      </section>
      {showForm && (
        <form className="appointment-form panel" onSubmit={(event) => void create(event)}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Alta coordinada</span>
              <h2>Nuevo turno</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() => setShowForm(false)}
              aria-label="Cerrar formulario"
            >
              <X size={19} />
            </button>
          </div>
          {patients.length === 0 ? (
            <p className="muted">Primero necesitás un paciente con vínculo activo.</p>
          ) : (
            <div className="form-grid">
              <label>
                Paciente
                <select
                  ref={patientSelectRef}
                  value={petId}
                  onChange={(event) => setPetId(event.target.value)}
                  required
                >
                  {patients.map((patient) => (
                    <option value={patient.id} key={patient.id}>
                      {patient.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Fecha y hora
                <input
                  type="datetime-local"
                  value={startsAt}
                  onChange={(event) => setStartsAt(event.target.value)}
                  required
                />
              </label>
              <label className="form-span-2">
                Motivo
                <input
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  minLength={2}
                  maxLength={160}
                  required
                />
              </label>
              <label>
                Duración
                <select
                  value={durationMinutes}
                  onChange={(event) => setDurationMinutes(Number(event.target.value))}
                >
                  <option value={30}>30 minutos</option>
                  <option value={45}>45 minutos</option>
                  <option value={60}>60 minutos</option>
                  <option value={90}>90 minutos</option>
                </select>
              </label>
              <label>
                Recordatorio
                <select
                  value={reminder ?? "none"}
                  onChange={(event) =>
                    setReminder(
                      event.target.value === "none"
                        ? null
                        : (Number(event.target.value) as AppointmentInput["reminderMinutesBefore"]),
                    )
                  }
                >
                  <option value={1440}>24 horas antes</option>
                  <option value={2880}>2 días antes</option>
                  <option value={10080}>1 semana antes</option>
                  <option value={60}>1 hora antes</option>
                  <option value="none">Sin recordatorio</option>
                </select>
              </label>
              <label>
                Sede
                <select
                  value={locationId}
                  onChange={(event) => {
                    setLocationId(event.target.value);
                    if (event.target.value) setLocation("");
                  }}
                >
                  <option value="">Sin sede / atención a domicilio</option>
                  {locations.map((site) => (
                    <option value={site.id} key={site.id}>
                      {site.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="form-span-2">
                {locationId ? "Referencia adicional" : "Ubicación"}
                <input
                  value={location}
                  onChange={(event) => setLocation(event.target.value)}
                  maxLength={200}
                  disabled={Boolean(locationId)}
                  placeholder={
                    locationId
                      ? "La sede elegida quedará como referencia del turno"
                      : "Consultorio o dirección general"
                  }
                />
              </label>
            </div>
          )}
          <div className="form-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </button>
            <button className="button button-primary" disabled={busy || patients.length === 0}>
              {busy ? <LoaderCircle className="spin" size={17} /> : <Check size={17} />} Guardar
              turno
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <div className="list-skeleton" role="status">
          <span />
          <span />
          <span />
        </div>
      ) : (
        <div className="agenda-layout">
          <section className="panel" aria-labelledby="upcoming-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Por atender</span>
                <h2 id="upcoming-title">Próximos turnos</h2>
              </div>
              <span className="count">{upcoming.length}</span>
            </div>
            {upcoming.length ? (
              <div className="appointment-list">
                {upcoming.map((appointment) => (
                  <AppointmentRow
                    appointment={appointment}
                    key={appointment.id}
                    actions={
                      <>
                        {appointment.status === "PLANNED" && (
                          <button
                            className="button-link"
                            disabled={busy}
                            onClick={() => void transition(appointment, "CONFIRMED")}
                          >
                            Confirmar
                          </button>
                        )}
                        <Link className="button-link button-link-success" to="/jornada">
                          Abrir jornada
                        </Link>
                        <button
                          className="button-link button-link-danger"
                          disabled={busy}
                          onClick={() => void transition(appointment, "CANCELLED")}
                        >
                          Cancelar
                        </button>
                      </>
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={CalendarDays}
                title="Sin turnos próximos"
                text="Creá un turno para un paciente autorizado."
              />
            )}
          </section>
          <section className="panel" aria-labelledby="history-agenda-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Trazabilidad</span>
                <h2 id="history-agenda-title">Historial reciente</h2>
              </div>
              <span className="count">{history.length}</span>
            </div>
            {history.length ? (
              <div className="appointment-list appointment-list-compact">
                {history
                  .slice(-20)
                  .reverse()
                  .map((appointment) => (
                    <AppointmentRow appointment={appointment} key={appointment.id} />
                  ))}
              </div>
            ) : (
              <p className="muted">Los turnos finalizados o cancelados aparecerán acá.</p>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function AccessRequestsScreen(): ReactNode {
  const { activeOrganization } = useCrm();
  const [accesses, setAccesses] = useState<CrmAccess[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [scope, setScope] = useState<AccessScope>("CLINICAL");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const identifierRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!activeOrganization) return;
    setLoading(true);
    setError(null);
    try {
      setAccesses(await api.accessRequests(activeOrganization.id));
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos cargar los vínculos."));
    } finally {
      setLoading(false);
    }
  }, [activeOrganization?.id]);

  useEffect(() => void load(), [load]);
  useEffect(() => {
    if (showForm) identifierRef.current?.focus();
  }, [showForm]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!activeOrganization) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const access = await api.requestAccess(activeOrganization.id, {
        identifier,
        scope,
        ...(message ? { message } : {}),
      });
      setSuccess(`La solicitud para ${access.petName} quedó pendiente de aprobación del tutor.`);
      setIdentifier("");
      setMessage("");
      setShowForm(false);
      await load();
    } catch (reason) {
      setError(errorMessage(reason, "No pudimos enviar la solicitud."));
    } finally {
      setBusy(false);
    }
  }

  const pending = accesses.filter((access) => access.status === "PENDING");
  const active = accesses.filter((access) => access.status === "ACTIVE");
  const closed = accesses.filter((access) => ["REJECTED", "REVOKED"].includes(access.status));

  return (
    <div className="screen">
      <PageHeader
        eyebrow="Consentimiento verificable"
        title="Vínculos con pacientes"
        text="La organización solicita; el tutor principal decide. No se crea una cartera por escanear un tag."
        action={
          <button className="button button-primary" onClick={() => setShowForm((value) => !value)}>
            <Plus size={17} /> Solicitar vínculo
          </button>
        }
      />
      <div className="consent-note">
        <ShieldCheck size={20} />
        <div>
          <strong>Privacidad por defecto</strong>
          <p>
            Hasta que el tutor apruebe, el CRM no puede abrir la ficha clínica ni agendar para esa
            mascota.
          </p>
        </div>
      </div>
      {error && <ErrorPanel message={error} retry={() => void load()} />}
      {success && (
        <div className="success-panel" role="status">
          <Check size={18} /> {success}
        </div>
      )}
      {showForm && (
        <form className="access-form panel" onSubmit={(event) => void submit(event)}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Nueva solicitud</span>
              <h2>Identificar paciente</h2>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={() => setShowForm(false)}
              aria-label="Cerrar formulario"
            >
              <X size={19} />
            </button>
          </div>
          <p className="form-intro">
            Usá el código público del perfil o el código impreso en el tag. No ingreses datos
            privados del tutor.
          </p>
          <div className="form-grid">
            <label className="form-span-2">
              Código público o tag
              <input
                ref={identifierRef}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                minLength={2}
                maxLength={120}
                placeholder="Ej. mora-7f3k o TAP-2048"
                required
              />
            </label>
            <label className="form-span-2">
              Alcance solicitado
              <select
                value={scope}
                onChange={(event) => setScope(event.target.value as AccessScope)}
              >
                <option value="READ_ONLY">Solo lectura</option>
                <option value="CLINICAL">Atención clínica</option>
                <option value="PRIMARY">Veterinaria habitual</option>
              </select>
              <small>
                {scope === "READ_ONLY"
                  ? "Consultar historia sin crear registros."
                  : scope === "CLINICAL"
                    ? "Consultar historia y organizar turnos; las escrituras exigen firma."
                    : "Consulta, agenda, recordatorios y seguimiento continuo."}
              </small>
            </label>
            <label className="form-span-2">
              Mensaje para el tutor <span>opcional</span>
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                maxLength={300}
                rows={3}
                placeholder="Explicá brevemente por qué solicitás el acceso."
              />
            </label>
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="button button-secondary"
              onClick={() => setShowForm(false)}
            >
              Cancelar
            </button>
            <button className="button button-primary" disabled={busy}>
              {busy ? <LoaderCircle className="spin" size={17} /> : <ShieldCheck size={17} />}{" "}
              Enviar al tutor
            </button>
          </div>
        </form>
      )}
      {loading ? (
        <div className="list-skeleton" role="status">
          <span />
          <span />
        </div>
      ) : (
        <div className="access-columns">
          <section className="panel" aria-labelledby="active-links-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Cartera vigente</span>
                <h2 id="active-links-title">Pacientes activos</h2>
              </div>
              <span className="count">{active.length}</span>
            </div>
            {active.length ? (
              <div className="access-list">
                {active.map((access) => (
                  <AccessRow access={access} key={access.id} />
                ))}
              </div>
            ) : (
              <p className="muted">Todavía no hay vínculos activos.</p>
            )}
          </section>
          <section className="panel" aria-labelledby="pending-links-title">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Esperando decisión</span>
                <h2 id="pending-links-title">Solicitudes pendientes</h2>
              </div>
              <span className="count">{pending.length}</span>
            </div>
            {pending.length ? (
              <div className="access-list">
                {pending.map((access) => (
                  <AccessRow access={access} key={access.id} />
                ))}
              </div>
            ) : (
              <p className="muted">No hay solicitudes pendientes.</p>
            )}
          </section>
          {closed.length > 0 && (
            <section className="panel access-history" aria-labelledby="closed-links-title">
              <div className="panel-heading">
                <div>
                  <span className="eyebrow">Historial</span>
                  <h2 id="closed-links-title">Cerrados</h2>
                </div>
                <span className="count">{closed.length}</span>
              </div>
              <div className="access-list">
                {closed.map((access) => (
                  <AccessRow access={access} key={access.id} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function AccessRow({ access }: { access: CrmAccess }) {
  return (
    <article className="access-row">
      <span className="patient-avatar" aria-hidden="true">
        {access.petName.charAt(0).toUpperCase()}
      </span>
      <div>
        <div>
          <h3>{access.petName}</h3>
          <span className={`status status-${access.status.toLowerCase()}`}>
            {access.status === "PENDING"
              ? "Pendiente"
              : access.status === "ACTIVE"
                ? "Activo"
                : access.status === "REJECTED"
                  ? "Rechazado"
                  : "Revocado"}
          </span>
        </div>
        <p>
          {patientKind(access.petSpecies)} · {SCOPE_LABEL[access.scope]}
        </p>
        <small>
          Solicitado por {access.requestedByName} · {formatDate(access.requestedAt)}
        </small>
      </div>
      {access.status === "ACTIVE" && (
        <Link to={`/pacientes/${access.petId}`} aria-label={`Abrir ficha de ${access.petName}`}>
          <ArrowRight size={18} />
        </Link>
      )}
    </article>
  );
}

function AppRoutes(): ReactNode {
  return (
    <Routes>
      <Route path="/ingresar" element={<LoginScreen />} />
      <Route path="/invitaciones/equipo" element={<TeamInvitationAcceptScreen />} />
      <Route element={<ProContextRoutes />}>
        <Route path="/acceso-veterinario" element={<VeterinaryAccessScreen />} />
        <Route path="/seleccionar" element={<WorkspaceSelectionScreen />} />
        <Route element={<Shell />}>
          <Route
            index
            element={
              <VeterinaryOnly>
                <DashboardScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/jornada"
            element={
              <VeterinaryOnly>
                <OperationsScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/agenda"
            element={
              <VeterinaryOnly>
                <AgendaScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/atenciones"
            element={
              <VeterinaryOnly>
                <ClinicalDraftsScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/pacientes"
            element={
              <VeterinaryOnly>
                <PatientsScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/pacientes/:petId"
            element={
              <VeterinaryOnly>
                <PatientDetailScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/vinculos"
            element={
              <VeterinaryOnly>
                <AccessRequestsScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/tareas"
            element={
              <VeterinaryOnly>
                <TasksScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/equipo"
            element={
              <VeterinaryOnly>
                <TeamScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/sedes"
            element={
              <VeterinaryOnly>
                <LocationsScreen />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/configuracion"
            element={
              <VeterinaryOnly>
                <WorkspaceSettingsScreen expected="VETERINARY" />
              </VeterinaryOnly>
            }
          />
          <Route
            path="/disponibilidad"
            element={
              <VeterinaryOnly>
                <Suspense fallback={<LoadingScreen />}>
                  <AvailabilityScreen expected="VETERINARY" />
                </Suspense>
              </VeterinaryOnly>
            }
          />
          {(["GROOMING", "TRAINING", "CARE"] as const).map((workspace) => (
            <Route
              key={`${workspace}-availability`}
              path={`/${workspace.toLowerCase()}/disponibilidad`}
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <AvailabilityScreen expected={workspace} />
                </Suspense>
              }
            />
          ))}
          <Route
            path="/grooming/configuracion"
            element={<WorkspaceSettingsScreen expected="GROOMING" />}
          />
          <Route
            path="/training/configuracion"
            element={<WorkspaceSettingsScreen expected="TRAINING" />}
          />
          <Route path="/care/configuracion" element={<WorkspaceSettingsScreen expected="CARE" />} />
          {(["GROOMING", "TRAINING", "CARE"] as const).map((workspace) => (
            <Route
              key={`${workspace}-team`}
              path={`${workspaceDefinition(workspace).homeRoute}/equipo`}
              element={<TeamScreen />}
            />
          ))}
          <Route
            path="/shelter/configuracion"
            element={<WorkspaceSettingsScreen expected="SHELTER" />}
          />
          <Route
            path="/commerce/configuracion"
            element={<WorkspaceSettingsScreen expected="COMMERCE" />}
          />
          <Route
            path="/partners/configuracion"
            element={<WorkspaceSettingsScreen expected="PARTNER" />}
          />
          {(["GROOMING", "TRAINING", "CARE"] as const).map((workspace) => (
            <Route
              key={workspace}
              path={`${workspaceDefinition(workspace).homeRoute}/*`}
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <OperationalWorkspaceScreen expected={workspace} />
                </Suspense>
              }
            />
          ))}
          <Route
            path="/shelter/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <ShelterWorkspaceScreen />
              </Suspense>
            }
          />
          <Route
            path="/commerce/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <CommercialWorkspaceScreen expected="COMMERCE" />
              </Suspense>
            }
          />
          <Route
            path="/partners/*"
            element={
              <Suspense fallback={<LoadingScreen />}>
                <CommercialWorkspaceScreen expected="PARTNER" />
              </Suspense>
            }
          />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ProContextRoutes(): ReactNode {
  return (
    <CrmProvider>
      <Outlet />
    </CrmProvider>
  );
}

function VeterinaryOnly({ children }: { children: ReactNode }): ReactNode {
  const { activeWorkspace } = useCrm();
  if (activeWorkspace && activeWorkspace.type !== "VETERINARY") {
    return <Navigate to={workspaceDefinition(activeWorkspace.type).homeRoute} replace />;
  }
  return children;
}

export function App(): ReactNode {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
